import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiPlus } from "react-icons/fi";
import { TailSpin } from "react-loader-spinner";
import toast from "react-hot-toast";

import {
  useCreateSurveyMutation,
  useSurveyDetailQuery,
  useUpdateSurveyMutation,
  type SurveyPlacement,
  type SurveyQuestion,
  type SurveyQuestionType,
  type SurveyWriteBody,
} from "../../lib/api";
import QuestionEditor from "./QuestionEditor";
import SurveyPageShell from "./SurveyPageShell";
import SurveyPreview from "./SurveyPreview";
import {
  blankQuestion,
  normalizeQuestions,
  retypeQuestion,
  uniqueKey,
  validateBuilder,
} from "./builderUtils";
import { PLACEMENT_OPTIONS, surveyErrorMessage } from "./surveyTypes";

const SurveyBuilder: React.FC = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const navigate = useNavigate();
  const isEditing = Boolean(surveyId);

  const { data: existing, isPending: loadingExisting } =
    useSurveyDetailQuery(surveyId);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [placements, setPlacements] = useState<SurveyPlacement[]>([
    "DASHBOARD_CTA",
  ]);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [focusIndex, setFocusIndex] = useState(0);
  const [showIssues, setShowIssues] = useState(false);

  // Hydrate once the existing survey arrives; `hydrated` keeps later refetches from
  // clobbering edits in progress.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (!existing || hydrated) return;
    setTitle(existing.title);
    setDescription(existing.description ?? "");
    setPlacements(existing.placements ?? []);
    setIsAnonymous(existing.isAnonymous);
    setQuestions(existing.questions ?? []);
    setHydrated(true);
  }, [existing, hydrated]);

  /**
   * Once a survey has been published its structure is frozen — responses already point at
   * these question keys and option ids. Wording and placements stay editable.
   *
   * ACTIVE and CLOSED are both frozen (the API answers 409 on a structural change to
   * either); DRAFT and SCHEDULED are still fully editable.
   */
  const locked = useMemo(() => {
    const status = String(existing?.status ?? "").toUpperCase();
    return status === "ACTIVE" || status === "CLOSED";
  }, [existing]);

  const issues = useMemo(
    () => validateBuilder(title, questions),
    [title, questions]
  );
  const invalidKeys = useMemo(
    () => new Set(issues.map((i) => i.questionKey).filter(Boolean) as string[]),
    [issues]
  );

  const createMutation = useCreateSurveyMutation();
  const updateMutation = useUpdateSurveyMutation();
  const saving = createMutation.isPending || updateMutation.isPending;

  /* ---------------------------------------------------------------- actions */

  const addQuestion = (type: SurveyQuestionType = "SINGLE_CHOICE") => {
    setQuestions((prev) => {
      const next = [
        ...prev,
        blankQuestion(type, prev.length, new Set(prev.map((q) => q.key))),
      ];
      setFocusIndex(next.length - 1);
      return next;
    });
  };

  const patchQuestion = (index: number, patch: Partial<SurveyQuestion>) =>
    setQuestions((prev) =>
      prev.map((q, i) => (i === index ? { ...q, ...patch } : q))
    );

  const retype = (index: number, type: SurveyQuestionType) =>
    setQuestions((prev) =>
      prev.map((q, i) => (i === index ? retypeQuestion(q, type) : q))
    );

  const move = (index: number, direction: -1 | 1) =>
    setQuestions((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      setFocusIndex(target);
      return next;
    });

  const duplicate = (index: number) =>
    setQuestions((prev) => {
      const source = prev[index];
      const copy: SurveyQuestion = {
        ...source,
        key: uniqueKey(
          source.prompt || source.key,
          new Set(prev.map((q) => q.key))
        ),
      };
      const next = [...prev];
      next.splice(index + 1, 0, copy);
      setFocusIndex(index + 1);
      return next;
    });

  const remove = (index: number) =>
    setQuestions((prev) => {
      const next = prev.filter((_, i) => i !== index);
      setFocusIndex((f) => Math.max(0, Math.min(f, next.length - 1)));
      return next;
    });

  const togglePlacement = (value: SurveyPlacement) =>
    setPlacements((prev) =>
      prev.includes(value) ? prev.filter((p) => p !== value) : [...prev, value]
    );

  const handleSave = () => {
    if (issues.length > 0) {
      setShowIssues(true);
      toast.error(issues[0].message);
      return;
    }

    const body: SurveyWriteBody = {
      title: title.trim(),
      description: description.trim() || null,
      placements,
      isAnonymous,
      questions: normalizeQuestions(questions),
    };

    if (isEditing && surveyId) {
      updateMutation.mutate(
        { surveyId, body },
        {
          onSuccess: () => {
            toast.success("Survey saved");
            navigate(`/surveys/${surveyId}`);
          },
          onError: (e) =>
            toast.error(surveyErrorMessage(e, "Could not save survey.")),
        }
      );
      return;
    }

    createMutation.mutate(body, {
      onSuccess: (res) => {
        toast.success("Survey created as a draft");
        navigate(res.data?._id ? `/surveys/${res.data._id}` : "/surveys");
      },
      onError: (e) =>
        toast.error(surveyErrorMessage(e, "Could not create survey.")),
    });
  };

  /* ----------------------------------------------------------------- render */

  if (isEditing && loadingExisting) {
    return (
      <SurveyPageShell>
        <div className="flex justify-center py-24">
          <TailSpin color="#0B75C2" height={40} width={40} />
        </div>
      </SurveyPageShell>
    );
  }

  return (
    <SurveyPageShell>
      <button
        type="button"
        onClick={() =>
          navigate(isEditing ? `/surveys/${surveyId}` : "/surveys")
        }
        className="mb-4 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#869AA9] transition hover:text-white"
      >
        <FiArrowLeft className="text-sm" />
        {isEditing ? "Back to survey" : "All surveys"}
      </button>

      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-sora text-xl font-semibold tracking-tight text-white">
            {isEditing ? "Edit survey" : "Create survey"}
          </h1>
          <p className="mt-1 max-w-xl text-[13px] leading-relaxed text-[#869AA9]">
            {isEditing
              ? "Changes save straight away. Publishing is done from the survey page."
              : "Build the questions, then publish when you are ready. It saves as a draft first."}
          </p>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex h-10 shrink-0 items-center justify-center rounded-xl bg-secondary_button px-5 text-[13px] font-semibold text-white shadow-[0_8px_24px_rgba(11,132,203,0.35)] transition hover:brightness-110 disabled:opacity-50"
        >
          {saving ? "Saving…" : isEditing ? "Save changes" : "Save draft"}
        </button>
      </header>

      {locked && (
        <p className="mb-5 rounded-xl border border-amber-500/35 bg-amber-500/[0.08] px-4 py-2.5 text-[12px] leading-relaxed text-amber-100/80">
          This survey is live, so questions, types and options are locked —
          changing them would invalidate answers already collected. Prompts and
          settings can still be edited.
        </p>
      )}

      {showIssues && issues.length > 0 && (
        <ul className="mb-5 space-y-1 rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-3">
          {issues.map((issue, i) => (
            <li key={i} className="text-[12px] text-red-100/95">
              {issue.message}
            </li>
          ))}
        </ul>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0 space-y-4">
          <section className="space-y-3 rounded-2xl border border-[#2C363E] bg-[#161E25]/40 p-4 sm:p-5">
            <div>
              <label
                htmlFor="survey-title"
                className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-[#5C6B76]"
              >
                Title
              </label>
              <input
                id="survey-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Product feedback — Q4"
                className="h-11 w-full rounded-lg border border-[#2C363E] bg-[#13181B] px-3 text-[14px] font-medium text-white outline-none transition placeholder:text-[#5C6B76] focus:border-secondary_button"
              />
            </div>

            <div>
              <label
                htmlFor="survey-description"
                className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-[#5C6B76]"
              >
                Intro text
              </label>
              <textarea
                id="survey-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                placeholder="Shown on the first screen, before the questions start."
                className="w-full resize-none rounded-lg border border-[#2C363E] bg-[#13181B] px-3 py-2.5 text-[13px] leading-relaxed text-[#E7EBEE] outline-none transition placeholder:text-[#5C6B76] focus:border-secondary_button"
              />
            </div>

            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#5C6B76]">
                Where it appears
              </p>
              <div className="flex flex-wrap gap-2">
                {PLACEMENT_OPTIONS.map((p) => {
                  const active = placements.includes(p.value);
                  return (
                    <button
                      key={String(p.value)}
                      type="button"
                      onClick={() => togglePlacement(p.value)}
                      title={p.hint}
                      className={`rounded-full border px-3.5 py-1.5 text-[12px] font-semibold transition ${
                        active
                          ? "border-secondary_button bg-secondary_button text-white"
                          : "border-[#2C363E] bg-[#1C2227] text-[#869AA9] hover:text-[#C6D0D7]"
                      }`}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <label className="flex cursor-pointer items-start gap-2.5 pt-1">
              <input
                type="checkbox"
                checked={isAnonymous}
                onChange={(e) => setIsAnonymous(e.target.checked)}
                className="mt-0.5 h-3.5 w-3.5 accent-secondary_button"
              />
              <span>
                <span className="block text-[13px] font-medium text-[#E7EBEE]">
                  Anonymous responses
                </span>
                <span className="mt-0.5 block text-[11px] leading-relaxed text-[#869AA9]">
                  Answers are not linked to a user. You see totals, not who said
                  what.
                </span>
              </span>
            </label>
          </section>

          {questions.map((q, i) => (
            <QuestionEditor
              key={q.key}
              question={q}
              index={i}
              total={questions.length}
              active={focusIndex === i}
              locked={locked}
              invalid={showIssues && invalidKeys.has(q.key)}
              onFocus={() => setFocusIndex(i)}
              onChange={(patch) => patchQuestion(i, patch)}
              onRetype={(type) => retype(i, type)}
              onMove={(direction) => move(i, direction)}
              onDuplicate={() => duplicate(i)}
              onRemove={() => remove(i)}
            />
          ))}

          {!locked && (
            <button
              type="button"
              onClick={() => addQuestion()}
              className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-[#3A4852] bg-[#161E25]/30 py-5 text-[13px] font-semibold text-[#869AA9] transition hover:border-secondary_button hover:text-white"
            >
              <FiPlus className="text-base" />
              Add question
            </button>
          )}

          {questions.length === 0 && (
            <p className="text-center text-[12px] text-[#5C6B76]">
              Questions get a stable key from their wording. Once the survey is
              published those keys are frozen, so answers stay readable.
            </p>
          )}
        </div>

        <div className="min-w-0">
          <SurveyPreview
            title={title}
            description={description}
            questions={questions}
            focusIndex={focusIndex}
            onFocusIndexChange={setFocusIndex}
          />
        </div>
      </div>
    </SurveyPageShell>
  );
};

export default SurveyBuilder;
